import {createSlice, Dispatch} from '@reduxjs/toolkit';
import {SelectedFlightService} from "../actions";
import {dailyFlights, setLoading} from "./daliyFlights";
import {addToSessionObject, BOOKING_SESSION_KEYS} from "../utils";

const formatAmount2 = (value: any): string => {
    const n = Number(value);
    if (!Number.isFinite(n)) return "0.00";
    return n.toFixed(2);
};

const toNumberOrNull = (value: any): number | null => {
    if (value == null || value === "") return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

const formatPrice = (value: any, symbol: string): string => {
    const amount = formatAmount2(value);
    return symbol ? `${amount}  ${symbol}` : amount;
};

const getPassengerFallbackPrice = (payload: any): number => {
    const total =
        toNumberOrNull(payload?.reservationTotalPrice) ??
        ((toNumberOrNull(payload?.departureTotalPrice) ?? 0) + (toNumberOrNull(payload?.returnTotalPrice) ?? 0));
    const passengerCount =
        (toNumberOrNull(payload?.numberOfAdults) ?? 0) +
        (toNumberOrNull(payload?.numberOfChildren) ?? 0) +
        (toNumberOrNull(payload?.numberOfInfants) ?? 0);

    return passengerCount > 0 ? total / passengerCount : total;
};

const getPassengerTypePrice = (
    payload: any,
    departureKey: string,
    returnKey: string,
    hasReturnFlight: boolean
): number => {
    const departurePrice = toNumberOrNull(payload?.[departureKey]);
    const returnPrice = toNumberOrNull(payload?.[returnKey]);

    if (departurePrice == null) {
        return getPassengerFallbackPrice(payload);
    }

    return departurePrice + (hasReturnFlight ? returnPrice ?? 0 : 0);
};

export const setDepartureFlight = (body: any) => async (dispatch: Dispatch) => {
    dispatch(departureFlight(body));
}

export const setReturnFlight = (body: any) => async (dispatch: Dispatch) => {
    dispatch(returnFlight(body));
}

export const clearFlights = () => (dispatch: Dispatch) => {
    dispatch(departureFlight(null));
    dispatch(returnFlight(null));
}

export const fetchPricePreview = (body: any) => async (dispatch: Dispatch): Promise<{ response?: any; couponValid?: boolean }> => {
    setLoading(true);
    try {
        const response = await SelectedFlightService.previewPrice(body);
        const hasCouponDiscount = response?.couponValidationResult?.valid === true;
        if (hasCouponDiscount && body) {
            const totalAmount = (Number(response.reservationTotalPrice) || 0);
            const symbol = response?.currencyResponse?.symbol ?? "";
            dispatch(setPricePreview(response));
            if (totalAmount >= 0) {
                dispatch(setPricesFromCouponPreview(response));
            } else {
                dispatch(setAdultPrice(response));
                dispatch(setChildPrice(response));
                dispatch(setInfantPrice(response));
            }
            return { response, couponValid: true };
        } else {
            dispatch(setPricePreview(response));
            dispatch(setAdultPrice(response));
            dispatch(setChildPrice(response));
            dispatch(setInfantPrice(response));
            return { response, couponValid: false };
        }
    } catch (err) {
        return {};
    } finally {
        setLoading(false);
    }
}

interface SelectedFlightSlice {
    departureFlight: any;
    returnFlight: any;
    pricePreview: number | any;
    pricePreviewDetails: any; // full preview response: departureTotalPrice, returnTotalPrice, reservationTotalPrice, totalTaxes, currencyResponse
    priceAdult: number | any;
    priceChild: number | any;
    priceInfant: number | any;
}

const initialState = (): SelectedFlightSlice => {
    let sessionData;

    try {
        sessionData = JSON.parse((window as any).sessionStorage.getItem(BOOKING_SESSION_KEYS.FLIGHTS));
    } catch (error) {
        sessionData = null;
        console.warn('There was a problem with sessionStorage (flights)', error);
    }

    return  {
        departureFlight: null,
        returnFlight: null,
        pricePreview: 0,
        pricePreviewDetails: null,
        priceAdult: 0,
        priceChild: 0,
        priceInfant: 0,
        ...sessionData
    };
};

const stateSlice = createSlice({
    name: 'bookflight/selected-flight',
    initialState: initialState(),
    reducers: {
        departureFlight: (state: any, action) => {
            addToSessionObject(BOOKING_SESSION_KEYS.FLIGHTS, "departureFlight", action.payload);
            state.departureFlight = action.payload;
        },
        returnFlight: (state: any, action) => {
            addToSessionObject(BOOKING_SESSION_KEYS.FLIGHTS, "returnFlight", action.payload);
            state.returnFlight = action.payload;
        },
        setPricePreview: (state: any, action) => {
            const p = action.payload;
            state.pricePreviewDetails = p;
            state.pricePreview = `${formatAmount2(p?.reservationTotalPrice)}  ${p?.currencyResponse?.symbol ?? ""}`;
        },
        /** Set displayed total from checkout response (e.g. after coupon applied). Payload: { totalAmount, currencySymbol } or string "amount  SYM". */
        setPriceFromCheckout: (state: any, action) => {
            const p = action.payload;
            if (typeof p === "string") {
                const trimmed = p.trim();
                const match = trimmed.match(/^([-+]?\d*\.?\d+)\s*(.*)$/);
                if (match) {
                    const amount = formatAmount2(match[1]);
                    const symbol = (match[2] || "").trim();
                    state.pricePreview = symbol ? `${amount}  ${symbol}` : amount;
                } else {
                    state.pricePreview = p;
                }
                return;
            }
            if (p?.totalAmount != null && p?.currencySymbol != null) {
                state.pricePreview = `${formatAmount2(p.totalAmount)}  ${p.currencySymbol}`;
            }
        },
        setAdultPrice: (state: any, action) => {
            const symbol = action.payload?.currencyResponse?.symbol ?? "";
            const price = getPassengerTypePrice(action.payload, "departureAdultPriceForOne", "returnAdultPriceForOne", !!state.returnFlight);
            state.priceAdult = formatPrice(price, symbol);
        },
        setChildPrice: (state: any, action) => {
            const symbol = action.payload?.currencyResponse?.symbol ?? "";
            const price = getPassengerTypePrice(action.payload, "departureChildPriceForOne", "returnChildPriceForOne", !!state.returnFlight);
            state.priceChild = formatPrice(price, symbol);
        },
        setInfantPrice: (state: any, action) => {
            const symbol = action.payload?.currencyResponse?.symbol ?? "";
            const price = getPassengerTypePrice(action.payload, "departureInfantPriceForOne", "returnInfantPriceForOne", !!state.returnFlight);
            state.priceInfant = formatPrice(price, symbol);
        },
        /** When coupon is applied, prefer *PriceWithCoupon fields; fallback to per-passenger average. */
        setPricesFromCouponPreview: (state: any, action) => {
            const p = action.payload || {};
            const fmt = (n: number) => formatAmount2(n);
            const symbol = p?.currencyResponse?.symbol ?? "";
            const totalAmount = Number(p?.reservationTotalPrice) || 0;
            const totalPax = (Number(p?.numberOfAdults) || 0) + (Number(p?.numberOfChildren) || 0) + (Number(p?.numberOfInfants) || 0);
            const fallbackPerPerson = totalPax > 0 ? totalAmount / totalPax : 0;
            const depAdult = p?.departureAdultPriceWithCoupon;
            const retAdult = p?.returnAdultPriceWithCoupon;
            const depChild = p?.departureChildPriceWithCoupon;
            const retChild = p?.returnChildPriceWithCoupon;
            const depInfant = p?.departureInfantPriceWithCoupon;
            const retInfant = p?.returnInfantPriceWithCoupon;

            state.pricePreview = `${fmt(totalAmount)}  ${symbol}`;
            state.priceAdult = `${fmt((depAdult != null ? Number(depAdult) : fallbackPerPerson) + (retAdult != null ? Number(retAdult) : 0))}  ${symbol}`;
            state.priceChild = `${fmt((depChild != null ? Number(depChild) : fallbackPerPerson) + (retChild != null ? Number(retChild) : 0))}  ${symbol}`;
            state.priceInfant = `${fmt((depInfant != null ? Number(depInfant) : fallbackPerPerson) + (retInfant != null ? Number(retInfant) : 0))}  ${symbol}`;
        }
    }
});

export const { departureFlight, returnFlight, setPricePreview, setPriceFromCheckout, setAdultPrice, setChildPrice, setInfantPrice, setPricesFromCouponPreview } = stateSlice.actions;

export default stateSlice.reducer;
